'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Bot,
  User,
  CheckCheck,
  Send,
  Sparkles,
  Phone,
  Video,
  MoreVertical,
  Paperclip,
  Smile,
  Instagram,
  Globe,
  ExternalLink,
  ShieldAlert,
  ArrowRight,
  ShoppingCart,
  Zap,
  RotateCcw,
  Check,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

type ChannelType = 'whatsapp' | 'instagram' | 'facebook';

interface ChatMessage {
  id: string;
  sender: 'customer' | 'ai' | 'agent';
  channel: ChannelType;
  text: string;
  timestamp: string;
  productCard?: {
    title: string;
    price: string;
    image: string;
    tag: string;
  };
  handoffNotice?: string;
}

const INITIAL_CONVERSATION: ChatMessage[] = [
  {
    id: '1',
    sender: 'customer',
    channel: 'instagram',
    text: 'Hey! Saw your story about the Zeric Pro Suite. Do you support Instagram DMs and WhatsApp together in one inbox?',
    timestamp: '10:24 AM',
  },
  {
    id: '2',
    sender: 'ai',
    channel: 'instagram',
    text: 'Hello! 👋 Yes, absolutely! Zeric unifies WhatsApp, Instagram DMs, and Facebook Messenger into a single real-time team inbox with 24/7 autonomous AI handling.',
    timestamp: '10:24 AM',
    productCard: {
      title: 'Zeric Omnichannel Pro Suite',
      price: '$49/mo · Unlimited Channels',
      image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&q=80',
      tag: 'Most Popular',
    },
  },
  {
    id: '3',
    sender: 'customer',
    channel: 'instagram',
    text: 'That looks awesome! Can I talk to an account manager about team onboarding?',
    timestamp: '10:25 AM',
  },
  {
    id: '4',
    sender: 'ai',
    channel: 'instagram',
    text: 'Certainly! I am routing your conversation to our Senior Solutions Lead right now.',
    timestamp: '10:25 AM',
    handoffNotice: 'AI Paused · Sarah Jenkins (Solutions Lead) assigned',
  },
  {
    id: '5',
    sender: 'agent',
    channel: 'instagram',
    text: 'Hi there! Sarah here from Zeric. I can set up a personalized 15-minute onboarding session for your team today. What time zone works best for you?',
    timestamp: '10:26 AM',
  },
];

const PRESET_PROMPTS = [
  {
    label: 'Ask for Product Catalog',
    channel: 'whatsapp' as ChannelType,
    customerText: 'Can you show me your available software plans and features?',
    aiReply:
      'Here are our current plans! Every tier includes our autonomous AI engine, visual flow builder, and unlimited agent seats.',
    productCard: {
      title: 'Enterprise Growth Tier',
      price: '$99/mo · Full AI Workflows',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=80',
      tag: 'Enterprise Ready',
    },
  },
  {
    label: 'Simulate Instagram DM',
    channel: 'instagram' as ChannelType,
    customerText: 'Hey @zeric, can we automate customer booking reminders on Instagram?',
    aiReply:
      'Yes! With Zeric Automation, you can schedule automated confirmation DMs, send calendar links, and answer booking queries in under 500ms.',
  },
  {
    label: 'Test Human Agent Takeover',
    channel: 'facebook' as ChannelType,
    customerText: 'I need custom billing assistance for a 50-person enterprise team.',
    aiReply:
      'I have flagged this as high-priority and escalated it to our Enterprise Billing Specialist.',
    handoffNotice: 'Priority Handoff · Escalated to Enterprise Billing Desk',
    agentReply:
      'Hello! Alex from Enterprise Billing here. I have generated a custom quote with volume licensing for 50 seats. Sending your proposal now!',
  },
];

export function RealtimeChatSimulator() {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_CONVERSATION);
  const [selectedChannel, setSelectedChannel] = useState<ChannelType | 'all'>('all');
  const [isTyping, setIsTyping] = useState(false);
  const [typingSender, setTypingSender] = useState<'ai' | 'agent' | null>(null);
  const [customInput, setCustomInput] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // Auto-scroll the chat container internally on new messages (never scrolls window)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, isTyping]);

  const handleSendCustomMessage = (textToSend?: string) => {
    const text = textToSend || customInput;
    if (!text.trim() || isTyping) return;

    const channel: ChannelType =
      selectedChannel === 'all' ? 'whatsapp' : selectedChannel;
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const newCustomerMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'customer',
      channel,
      text: text.trim(),
      timestamp: now,
    };

    setMessages((prev) => [...prev, newCustomerMsg]);
    setCustomInput('');
    setIsTyping(true);
    setTypingSender('ai');

    // Simulate AI reasoning and smart response
    setTimeout(() => {
      let aiText = `Thank you for your message! Zeric AI processes incoming customer inquiries instantly across WhatsApp, Instagram, and Facebook Messenger with 99.4% accuracy.`;
      const lower = text.toLowerCase();

      if (lower.includes('price') || lower.includes('cost') || lower.includes('plan')) {
        aiText = `Our plans start at just $19/month and scale with your team! All tiers include official Meta Graph API connectivity, Instagram DMs, and our autonomous AI bot.`;
      } else if (lower.includes('instagram') || lower.includes('dm')) {
        aiText = `Instagram DMs are handled natively in Zeric! When a customer messages your Instagram Business profile, our AI responds automatically and syncs the thread to your live CRM inbox.`;
      } else if (lower.includes('human') || lower.includes('agent') || lower.includes('speak') || lower.includes('help')) {
        aiText = `I understand! Let me hand you over to an active team agent right away.`;
      }

      const aiResponseMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        channel,
        text: aiText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, aiResponseMsg]);
      setIsTyping(false);
      setTypingSender(null);
    }, 900);
  };

  const handleApplyPreset = (preset: typeof PRESET_PROMPTS[0]) => {
    if (isTyping) return;
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const customerMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'customer',
      channel: preset.channel,
      text: preset.customerText,
      timestamp: now,
    };

    setMessages((prev) => [...prev, customerMsg]);
    setIsTyping(true);
    setTypingSender('ai');

    setTimeout(() => {
      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        channel: preset.channel,
        text: preset.aiReply,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        productCard: preset.productCard,
        handoffNotice: preset.handoffNotice,
      };

      setMessages((prev) => [...prev, aiMsg]);

      if (preset.agentReply) {
        setTypingSender('agent');
        setTimeout(() => {
          const agentMsg: ChatMessage = {
            id: (Date.now() + 2).toString(),
            sender: 'agent',
            channel: preset.channel,
            text: preset.agentReply!,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          };
          setMessages((prev) => [...prev, agentMsg]);
          setIsTyping(false);
          setTypingSender(null);
        }, 1200);
      } else {
        setIsTyping(false);
        setTypingSender(null);
      }
    }, 850);
  };

  const filteredMessages =
    selectedChannel === 'all'
      ? messages
      : messages.filter((m) => m.channel === selectedChannel);

  return (
    <div className="relative mx-auto max-w-6xl w-full text-left">
      {/* Glow Backdrop */}
      <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl opacity-60 pointer-events-none -z-10" />

      {/* Main Container Card */}
      <div className="relative rounded-2xl border border-border/80 bg-card/95 shadow-2xl overflow-hidden backdrop-blur-xl ring-1 ring-white/10 dark:ring-white/5">
        
        {/* Browser / Mac Chrome Bar */}
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/40 px-4 py-3 backdrop-blur-md select-none">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-full bg-red-500/80" />
            <div className="size-3 rounded-full bg-yellow-500/80" />
            <div className="size-3 rounded-full bg-green-500/80" />
            <span className="ml-2 text-xs font-semibold text-muted-foreground hidden sm:inline">
              Zeric Live Omnichannel Console
            </span>
          </div>

          {/* Channel Selector Filter */}
          <div className="flex items-center gap-1 bg-background/80 p-1 rounded-xl border border-border/60 text-xs">
            <button
              type="button"
              onClick={() => setSelectedChannel('all')}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                selectedChannel === 'all'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Unified Feed ({messages.length})
            </button>
            <button
              type="button"
              onClick={() => setSelectedChannel('whatsapp')}
              className={`px-2.5 py-1 rounded-lg font-medium flex items-center gap-1.5 transition-all ${
                selectedChannel === 'whatsapp'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="size-2 rounded-full bg-emerald-500" />
              WhatsApp
            </button>
            <button
              type="button"
              onClick={() => setSelectedChannel('instagram')}
              className={`px-2.5 py-1 rounded-lg font-medium flex items-center gap-1.5 transition-all ${
                selectedChannel === 'instagram'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Instagram className="size-3" />
              Instagram
            </button>
            <button
              type="button"
              onClick={() => setSelectedChannel('facebook')}
              className={`px-2.5 py-1 rounded-lg font-medium flex items-center gap-1.5 transition-all hidden md:flex ${
                selectedChannel === 'facebook'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Globe className="size-3" />
              Messenger
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px] font-semibold gap-1">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Realtime Sync
            </Badge>
          </div>
        </div>

        {/* Dashboard 3-Pane Body */}
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[580px] bg-background">
          
          {/* LEFT COLUMN: Channels & Conversations List (Hidden on mobile) */}
          <div className="hidden lg:block lg:col-span-4 border-r border-border/50 bg-muted/20 p-3 space-y-2 select-none">
            <div className="px-2 py-2 flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Active Inbox Streams
              </h4>
              <Badge className="bg-primary/10 text-primary text-[10px] font-mono border-none">
                3 Connected
              </Badge>
            </div>

            {/* Conversation 1: Instagram (Active) */}
            <div className="p-3 rounded-xl border border-pink-500/30 bg-card shadow-xs cursor-pointer relative overflow-hidden transition-all hover:border-pink-500/50">
              <div className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-purple-600 to-pink-600" />
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="size-9 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500 text-white flex items-center justify-center font-bold text-xs shadow-xs">
                      <Instagram className="size-4" />
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-foreground">@maya_studio</span>
                      <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[9px] h-3.5 px-1 font-semibold border-none">
                        Instagram
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate max-w-[150px]">
                      Sarah: What time zone works best...
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-medium text-muted-foreground">Just now</span>
              </div>
            </div>

            {/* Conversation 2: WhatsApp */}
            <div className="p-3 rounded-xl border border-border/60 bg-card/60 hover:bg-card hover:border-emerald-500/30 transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="size-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shadow-xs">
                      WA
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-foreground">Carlos Martinez</span>
                      <Badge className="bg-emerald-600 text-white text-[9px] h-3.5 px-1 font-semibold border-none">
                        WhatsApp
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate max-w-[150px]">
                      AI: Here are our current plans...
                    </p>
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground">3m ago</span>
              </div>
            </div>

            {/* Conversation 3: Facebook */}
            <div className="p-3 rounded-xl border border-border/60 bg-card/60 hover:bg-card hover:border-blue-500/30 transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="size-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs shadow-xs">
                    FB
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-foreground">Acme Corp Page</span>
                      <Badge className="bg-blue-600 text-white text-[9px] h-3.5 px-1 font-semibold border-none">
                        Page
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate max-w-[150px]">
                      Alex: Volume licensing proposal...
                    </p>
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground">12m ago</span>
              </div>
            </div>

            {/* Live Stats Box */}
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 space-y-2 mt-4">
              <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                <Zap className="size-3.5 text-primary" />
                Zeric AI Engine Status
              </div>
              <div className="grid grid-cols-2 gap-2 text-center text-xs">
                <div className="bg-background/80 p-1.5 rounded-lg border border-border/60">
                  <div className="font-mono font-bold text-primary">340ms</div>
                  <div className="text-[10px] text-muted-foreground">Avg Response</div>
                </div>
                <div className="bg-background/80 p-1.5 rounded-lg border border-border/60">
                  <div className="font-mono font-bold text-emerald-600">89.2%</div>
                  <div className="text-[10px] text-muted-foreground">Autonomous</div>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER & RIGHT COLUMN: Active Chat Conversation */}
          <div className="col-span-1 lg:col-span-8 flex flex-col h-full bg-background/90">
            
            {/* Chat Header */}
            <div className="p-4 border-b border-border/50 flex items-center justify-between bg-card/60 backdrop-blur-md">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="size-10 rounded-full bg-gradient-to-tr from-purple-600 via-pink-600 to-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                    MS
                  </div>
                  <span className="absolute bottom-0 right-0 size-3 rounded-full bg-emerald-500 ring-2 ring-background" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-foreground">Maya Studio</h3>
                    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] px-1.5 py-0 h-4 font-semibold border-none flex items-center gap-1">
                      <Instagram className="size-2.5" />
                      @maya_studio
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-emerald-500" />
                    Handled by <strong className="text-foreground font-medium">Zeric AI</strong> + <strong className="text-foreground font-medium">Sarah Jenkins</strong>
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMessages(INITIAL_CONVERSATION)}
                  className="h-8 text-xs gap-1 text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="size-3" />
                  Reset
                </Button>
                <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 text-[11px] font-medium hidden sm:inline-flex">
                  Meta Graph API v21.0
                </Badge>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div ref={messagesContainerRef} className="flex-1 p-4 sm:p-5 space-y-4 overflow-y-auto max-h-[380px] bg-muted/5 relative">
              {filteredMessages.map((msg) => {
                const isCustomer = msg.sender === 'customer';
                const isAi = msg.sender === 'ai';
                const isAgent = msg.sender === 'agent';

                return (
                  <div key={msg.id} className="space-y-2">
                    {/* Handoff Notice Banner if present */}
                    {msg.handoffNotice && (
                      <div className="my-2 flex justify-center">
                        <div className="bg-primary/10 border border-primary/20 text-primary px-3 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1.5 shadow-xs animate-in fade-in">
                          <Sparkles className="size-3" />
                          {msg.handoffNotice}
                        </div>
                      </div>
                    )}

                    <div className={`flex items-end gap-2 ${isCustomer ? 'justify-end' : 'justify-start'}`}>
                      {/* Avatar for AI / Agent */}
                      {!isCustomer && (
                        <div
                          className={`size-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow-xs ${
                            isAi ? 'bg-primary' : 'bg-indigo-600'
                          }`}
                        >
                          {isAi ? <Bot className="size-4" /> : <User className="size-4" />}
                        </div>
                      )}

                      <div className={`max-w-[85%] sm:max-w-[75%] space-y-2`}>
                        {/* Sender Label */}
                        {!isCustomer && (
                          <div className="text-[10px] font-bold text-muted-foreground flex items-center gap-1.5 pl-1">
                            {isAi ? (
                              <span className="text-primary flex items-center gap-1">
                                <Sparkles className="size-2.5" /> Zeric AI Autonomous Engine
                              </span>
                            ) : (
                              <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                                Sarah Jenkins (Human Agent)
                              </span>
                            )}
                            <span>·</span>
                            <span>{msg.timestamp}</span>
                          </div>
                        )}

                        {/* Message Bubble */}
                        <div
                          className={`p-3.5 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-xs ${
                            isCustomer
                              ? 'bg-primary text-primary-foreground rounded-br-xs'
                              : isAi
                              ? 'bg-card border border-border/80 text-foreground rounded-bl-xs'
                              : 'bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 text-foreground rounded-bl-xs'
                          }`}
                        >
                          <p>{msg.text}</p>

                          {/* Interactive Product Card Simulation */}
                          {msg.productCard && (
                            <div className="mt-3 p-3 bg-muted/40 rounded-xl border border-border/80 space-y-2.5 text-left text-foreground">
                              <div className="flex items-center gap-3">
                                <img
                                  src={msg.productCard.image}
                                  alt={msg.productCard.title}
                                  className="size-14 rounded-lg object-cover border border-border shrink-0"
                                />
                                <div className="min-w-0">
                                  <Badge className="bg-primary/10 text-primary text-[9px] font-bold border-none mb-1">
                                    {msg.productCard.tag}
                                  </Badge>
                                  <h5 className="text-xs font-bold truncate">
                                    {msg.productCard.title}
                                  </h5>
                                  <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                    {msg.productCard.price}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="w-full h-7 text-xs bg-primary hover:bg-primary/90 text-primary-foreground gap-1 font-medium"
                                  onClick={() =>
                                    handleSendCustomMessage(
                                      'I would like to start my trial for the Omnichannel Pro Suite!'
                                    )
                                  }
                                >
                                  <ShoppingCart className="size-3" />
                                  Instant 1-Click Order
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Time & Read Receipts for customer */}
                          {isCustomer && (
                            <div className="text-[10px] text-primary-foreground/70 mt-1 flex items-center justify-end gap-1">
                              <span>{msg.timestamp}</span>
                              <CheckCheck className="size-3 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex items-center gap-2">
                  <div
                    className={`size-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                      typingSender === 'agent' ? 'bg-indigo-600' : 'bg-primary'
                    }`}
                  >
                    {typingSender === 'agent' ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
                  </div>
                  <div className="bg-card border border-border/80 px-3.5 py-2 rounded-2xl rounded-bl-xs shadow-xs flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground text-[11px]">
                      {typingSender === 'agent' ? 'Sarah is typing' : 'Zeric AI is generating response'}
                    </span>
                    <span className="size-1.5 rounded-full bg-primary animate-bounce delay-0" />
                    <span className="size-1.5 rounded-full bg-primary animate-bounce delay-150" />
                    <span className="size-1.5 rounded-full bg-primary animate-bounce delay-300" />
                  </div>
                </div>
              )}

              {/* End anchor */}
              <div />
            </div>

            {/* Quick Interactive Prompt Chips */}
            <div className="p-3 border-t border-border/50 bg-muted/15 flex items-center gap-2 overflow-x-auto select-none no-scrollbar">
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider shrink-0 flex items-center gap-1">
                <Sparkles className="size-3 text-primary" /> Test Live:
              </span>
              {PRESET_PROMPTS.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleApplyPreset(preset)}
                  disabled={isTyping}
                  className="px-2.5 py-1 rounded-lg bg-background hover:bg-card border border-border/80 text-xs font-medium text-foreground hover:border-primary/50 transition-all shrink-0 shadow-xs flex items-center gap-1.5 disabled:opacity-50"
                >
                  {preset.channel === 'whatsapp' ? (
                    <span className="size-1.5 rounded-full bg-emerald-500" />
                  ) : preset.channel === 'instagram' ? (
                    <Instagram className="size-2.5 text-pink-500" />
                  ) : (
                    <Globe className="size-2.5 text-blue-500" />
                  )}
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Interactive Chat Input Bar */}
            <div className="p-3.5 border-t border-border/50 bg-card/80">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendCustomMessage();
                }}
                className="flex items-center gap-2"
              >
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Type a simulated customer message (e.g., Do you support WhatsApp catalogs?)..."
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    disabled={isTyping}
                    className="w-full bg-muted/40 border border-border/70 rounded-xl px-4 py-2.5 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                  />
                </div>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!customInput.trim() || isTyping}
                  className="h-10 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl gap-1.5 shadow-xs"
                >
                  <span>Send</span>
                  <Send className="size-3.5" />
                </Button>
              </form>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
}
